import React, { useEffect } from "react";
import i18n from "i18n";
import { setScheme } from "@services/api";
import { useSelectedScheme } from "@hooks/useSelectedScheme";
import { useLocalStorage } from "@hooks/useLocalStorage";
import { useAllAccountingPeriods } from "@hooks/query/zenith/useAllAccountingPeriods";
import type { LoggedInInfo, AccountingPeriod } from "../types";

type AppContextProps = {
  loggedInInfo: LoggedInInfo;
  notifications: any[];
  setLoggedInInfo: (info: LoggedInInfo) => void;
  setNotifications: (notifications: any[]) => void;
  clearLoggedInInfo: () => void;
  checkPermissions: (permissions: string[]) => boolean;
  isExpandSidebar: boolean;
  setIsExpandSidebar: any;
  isExpandInfoPanel: boolean;
  setIsExpandInfoPanel: any;
  infoPanel: React.ReactNode;
  setInfoPanel: any;
  setLanguage: (language: string) => void;
  currentAccountingPeriod: AccountingPeriod;
  /** The selected scheme's periods have loaded and none is active, so period-scoped lists come back empty. */
  isBetweenPeriods: boolean;
  schemeId?: string;
  selectScheme: (id: string | null) => void;
};

const AppContext = React.createContext<AppContextProps | undefined>(undefined);

// Before sign-in, for admins, and in a scheme between accounting periods.
const NO_PERIOD = { id: "", periodStart: new Date(), periodEnd: new Date() };

const AppContextProvider: React.FunctionComponent<{
  children: React.ReactNode;
}> = props => {
  const [notifications, setNotifications] = React.useState<any[]>([]);
  const [loggedInInfo, setLoggedInInfo] = React.useState<LoggedInInfo>({
    isLoggedIn: false,
  });
  const [isExpandSidebar, setIsExpandSidebar] = useLocalStorage(
    "isExpandSidebar",
    true,
  );
  const [isExpandInfoPanel, setIsExpandInfoPanel] = useLocalStorage(
    "isExpandInfoPanel",
    true,
  );

  const [infoPanel, setInfoPanel] = React.useState<React.ReactNode>();
  // The scheme Zenith shows: the one selected in the app bar, else home.
  // Queries are cached per scheme (App.tsx), so a switch needs no reset.
  const homeSchemeId = loggedInInfo?.userDetails?.subscriber?.level1ResourceId;
  const schemeId = useSelectedScheme() ?? homeSchemeId;

  const checkPermissions = (permissions: string[]): boolean => {
    const userPermissions =
      loggedInInfo?.userDetails?.permissions?.map(
        (permission: any) => permission.name,
      ) || [];

    if (!permissions?.length) return true;

    return (
      userPermissions.length > 0 &&
      permissions.every(
        permission => userPermissions.indexOf(permission) !== -1,
      )
    );
  };

  const setLanguage = (language: string) => {
    setLoggedInInfo({
      ...loggedInInfo,
      userDetails: {
        ...loggedInInfo?.userDetails,
        language,
      },
    });
  };

  const clearLoggedInInfo = () => {
    setLoggedInInfo({
      isLoggedIn: false,
    });
    setNotifications([]);
    setScheme(null);
  };

  useEffect(() => {
    if (loggedInInfo?.userDetails?.language) {
      i18n.changeLanguage(loggedInInfo?.userDetails?.language);
    }
  }, [loggedInInfo?.userDetails?.language]);

  const { data: activePeriods } = useAllAccountingPeriods({
    params: { isActive: true, level1ResourceId: schemeId },
    enabled: loggedInInfo.isLoggedIn && Boolean(schemeId),
    // This provider outlives a switch, so the last scheme's period would
    // otherwise stand in until the new scheme's arrives.
    keepPreviousData: false,
  });
  // Derived, not stored: a scheme between periods has none, rather than
  // keeping the previous scheme's.
  const currentAccountingPeriod = React.useMemo(() => {
    const [active] = activePeriods ?? [];
    return active
      ? {
          id: active.id,
          periodStart: new Date(active.periodStart),
          periodEnd: new Date(active.periodEnd),
        }
      : NO_PERIOD;
  }, [activePeriods]);

  return (
    <AppContext.Provider
      value={{
        loggedInInfo,
        setLoggedInInfo,
        notifications,
        setNotifications,
        clearLoggedInInfo,
        checkPermissions,
        isExpandSidebar,
        setIsExpandSidebar,
        isExpandInfoPanel,
        setIsExpandInfoPanel,
        infoPanel,
        setInfoPanel,
        setLanguage,
        currentAccountingPeriod,
        isBetweenPeriods: activePeriods?.length === 0,
        schemeId,
        // Home is stored as no selection, so it keeps one cache whether
        // reached by default or picked in the switcher.
        selectScheme: (id: string | null) =>
          setScheme(id === homeSchemeId ? null : id),
      }}
    >
      {props.children}
    </AppContext.Provider>
  );
};

export const useAppContext = () => {
  const context = React.useContext(AppContext);
  if (context === undefined) {
    throw new Error("useAppContext must be used within a AppContextProvider");
  }
  return context;
};

export default AppContextProvider;
